<template>
  <Builder
    v-if="!loading"
    v-model="toggleValue"
    :settings="settings"
  >
    <template #actions>
      <TemplateActions
        :loading="isLoading"
        @save="onSave"
      />
    </template>
  </Builder>
</template>

<script
  lang="ts"
  setup
>
import { useQuery } from '@vue/apollo-composable';
import gql from 'graphql-tag';
import { storeToRefs } from 'pinia';
import { computed, reactive, ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';
import eventBus from '@/event-bus';
import { emailTemplateFragment } from '@/graphql-fragments';
import { flash, modal } from '@/helpers/ui';
import Builder from '@/modules/marketing/email/builder/index.vue';
import { useCompanyStore } from '@/stores/company';
import { useOnboardingStore } from '@/stores/onboarding';
import { useMarketingStore } from '../../../store';
import TemplateActions from '../../TemplateActions.vue';

const { companySettings, updateCompany } = useCompanyStore();
const { currentOnboardingFlow } = toRefs(useOnboardingStore());
const { t } = useI18n();
const { setAttrs, updateEmailTemplate } = useMarketingStore();
const { isInvalid, isSubmitted } = storeToRefs(useMarketingStore());

const isLoading = ref(false);
const settings = reactive({
  emailType: 'REBOOK_REMINDER',
  title: t('marketing.email.automated_emails.rebook.title'),
  description: t('marketing.email.automated_emails.rebook.intro'),
  sentDescription: t('marketing.email.automated_emails.rebook_setup.when'),
  receiveDescription: t('marketing.email.automated_emails.rebook.receive_info'),
  showToggle: true,
  showRebookSettings: true,
  showReset: true,
  hideButtonOptions: true,
  allowImageUpload: false,
});

const { loading, result, onResult } = useQuery(
  gql`
    query getEmailTemplate($emailType: EmailTemplateEnum!) {
      emailTemplate(emailType: $emailType) {
        ...emailTemplate
      }
      emailTemplateDefault: emailTemplate(
        emailType: $emailType
        defaultValues: true
      ) {
        ...emailTemplate
      }
    }
    ${emailTemplateFragment}
  `,
  {
    emailType: 'REBOOK_REMINDER',
  },
);

onResult(() => {
  if (result.value.emailTemplate) {
    setAttrs({
      ...result.value.emailTemplate,
      emailTemplateDefault: result.value.emailTemplateDefault,
    });
  }
});

const toggleValue = computed({
  get() {
    return companySettings.bookings?.rebookReminderEnabled || false;
  },
  set(value) {
    const input = {
      settings: {
        bookings: {
          rebookReminderEnabled: value,
        },
      },
    };

    updateCompany(input).then(() => {
      if (currentOnboardingFlow.value === 'marketing') {
        eventBus.$emit('open-task-menu');
        currentOnboardingFlow.value = '';
      }
      flash(
        t(`global.flash.rebook_reminder_${value ? 'enabled' : 'disabled'}`),
      );
    });
  },
});

const onSave = () => {
  isSubmitted.value = true;

  if (isInvalid.value) {
    return;
  }

  modal('confirmation').then(() => {
    isLoading.value = true;
    updateEmailTemplate('REBOOK_REMINDER').finally(() => {
      if (currentOnboardingFlow.value === 'marketing') {
        eventBus.$emit('open-task-menu');
        currentOnboardingFlow.value = '';
      }

      flash(t('global.flash.email_saved'));
      isSubmitted.value = false;
      isLoading.value = false;
    });
  });
};
</script>
